/**
 * useMyBookings Hook
 *
 * Fetches and manages user's bookings from Firestore.
 */

import { useState, useEffect, useMemo } from 'react';
import { db } from '../../../services/firebase';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { useAuth } from '../../../contexts';

export type BookingStatus = 'pending' | 'confirmed' | 'completed' | 'cancelled' | 'declined';
export type BookingFilter = 'upcoming' | 'past' | 'cancelled' | 'all';

export interface BookingWithListing {
  id: string;
  listingId: string;
  guestId: string;
  hostId: string;
  checkIn: Date;
  checkOut: Date;
  nights: number;
  guests: {
    adults: number;
    children: number;
    infants: number;
  };
  pricing: {
    nightlyRate: number;
    nightlyTotal: number;
    cleaningFee: number;
    platformCommission: number;
    totalAmount: number;
    currency: string;
  };
  status: BookingStatus;
  paymentStatus: 'pending' | 'partial' | 'paid' | 'refunded';
  specialRequests?: string;
  createdAt: Date;
  confirmedAt?: Date;
  hasReview?: boolean;
  listing: {
    id: string;
    title: string;
    photo: string;
    location: string;
    propertyType: string;
  };
  host: {
    id: string;
    name: string;
    photo?: string;
  };
}

export const useMyBookings = (filter: BookingFilter = 'all') => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [bookings, setBookings] = useState<BookingWithListing[]>([]);
  const [fetchKey, setFetchKey] = useState(0);

  useEffect(() => {
    const fetchBookings = async () => {
      if (!user) {
        setBookings([]);
        setIsLoading(false);
        return;
      }

      try {
        // Query bookings for this user
        const bookingsRef = collection(db, 'bookings');
        const q = query(
          bookingsRef,
          where('guestId', '==', user.uid),
          orderBy('checkIn', 'desc')
        );

        const snapshot = await getDocs(q);

        if (snapshot.empty) {
          setBookings([]);
          setIsLoading(false);
          return;
        }

        // Transform the data
        const bookingsData: BookingWithListing[] = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            listingId: data.listingId,
            guestId: data.guestId,
            hostId: data.hostId,
            checkIn: data.checkIn?.toDate() || new Date(),
            checkOut: data.checkOut?.toDate() || new Date(),
            nights: data.nights || 1,
            guests: data.guests || { adults: 1, children: 0, infants: 0 },
            pricing: data.pricing || {
              nightlyRate: 0,
              nightlyTotal: 0,
              cleaningFee: 0,
              platformCommission: 0,
              totalAmount: 0,
              currency: 'USD',
            },
            status: data.status || 'pending',
            paymentStatus: data.paymentStatus || 'pending',
            specialRequests: data.specialRequests,
            createdAt: data.createdAt?.toDate() || new Date(),
            confirmedAt: data.confirmedAt?.toDate(),
            hasReview: data.hasReview || false,
            listing: data.listing || {
              id: data.listingId,
              title: 'Unknown Listing',
              photo: '',
              location: 'Lebanon',
              propertyType: 'Property',
            },
            host: data.host || {
              id: data.hostId,
              name: 'Host',
            },
          };
        });

        setBookings(bookingsData);
      } catch (error) {
        console.error('Error fetching bookings:', error);
        setBookings([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBookings();
  }, [user, fetchKey]);

  // Filter bookings based on status
  const filteredBookings = useMemo(() => {
    const now = new Date();

    switch (filter) {
      case 'upcoming':
        return bookings.filter(
          (b) =>
            (b.status === 'pending' || b.status === 'confirmed') &&
            b.checkIn > now
        );
      case 'past':
        return bookings.filter((b) => b.status === 'completed');
      case 'cancelled':
        return bookings.filter(
          (b) => b.status === 'cancelled' || b.status === 'declined'
        );
      default:
        return bookings;
    }
  }, [bookings, filter]);

  // Stats
  const stats = useMemo(() => {
    const now = new Date();
    return {
      upcoming: bookings.filter(
        (b) =>
          (b.status === 'pending' || b.status === 'confirmed') &&
          b.checkIn > now
      ).length,
      total: bookings.length,
      needsReview: bookings.filter(
        (b) => b.status === 'completed' && !b.hasReview
      ).length,
    };
  }, [bookings]);

  return {
    bookings: filteredBookings,
    isLoading,
    stats,
    refetch: () => setFetchKey((k) => k + 1),
  };
};

export default useMyBookings;
